


import './AboutUs.css';
import React, { useState, useEffect } from 'react';
import { aboutUsService } from '../../../../Service/aboutUsService';
import { useLoader } from '../../../../context/LoaderProvider';

const AboutUs = () => {
  const [aboutUsId, setAboutUsId] = useState(null);
  const [aboutUsText, setAboutUsText] = useState('');
  const [aboutUsImage, setAboutUsImage] = useState('');
  const [tempImage, setTempImage] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [tempText, setTempText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [hasData, setHasData] = useState(false);
  const [charLimit, setCharLimit] = useState(500); // Default character limit

  const { showLoader, hideLoader } = useLoader();
  const vendorId = localStorage.getItem("vendorId");
  const token = localStorage.getItem("authToken");

  // Helper function to get full image URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('food/')) {
      return `https://backend.maamaas.com/${imagePath}`;
    }
    return `https://backend.maamaas.com/food/${imagePath}`;
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => setSnackbar({ show: false, message: '', type: 'success' }), 3000);
  };

  // Save Everything (Both Image and Text)
  const handleSave = async () => {
    if (!tempText.trim()) {
      showNotification("Please enter about us text", "warning");
      return;
    }

    if (tempText.length > charLimit) {
      showNotification(`Description exceeds ${charLimit} characters limit`, "warning");
      return;
    }

    setLoading(true);
    showLoader();

    try {
      if (!hasData || !aboutUsId) {
        // Create new about us
        const response = await aboutUsService.createAboutUs(tempText, tempImageFile);
        if (response && response.aboutUsId) {
          setAboutUsId(response.aboutUsId);
          setHasData(true);
          setAboutUsText(tempText);
          if (response.image) {
            setAboutUsImage(getFullImageUrl(response.image));
          }
          showNotification("About Us created successfully!", "success");
        }
      } else {
        // Update existing about us
        if (tempImageFile) {
          // Update both image and text
          const response = await aboutUsService.updateAboutUs(
            aboutUsId,
            tempText,
            tempImageFile,
            aboutUsImage
          );
          if (response && response.image) {
            setAboutUsImage(getFullImageUrl(response.image));
          }
        } else {
          // Update only text
          await aboutUsService.updateAboutUsText(aboutUsId, tempText);
        }
        setAboutUsText(tempText);
        showNotification("About Us updated successfully!", "success");
      }

      setShowModal(false);
      setTempImage(null);
      setTempImageFile(null);
      await fetchAboutUs();
    } catch (error) {
      console.error("Failed to save about us:", error);
      showNotification(error.message || "Failed to save about us", "error");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // Fetch About Us data
  const fetchAboutUs = async () => {
    if (!vendorId || !token) {
      return;
    }

    try {
      showLoader();
      const data = await aboutUsService.getAboutUs();

      if (data && data.aboutUsId) {
        setHasData(true);
        setAboutUsId(data.aboutUsId);
        setAboutUsText(data.aboutUs || '');
        let imageUrl = data.image || '';
        if (imageUrl && !imageUrl.startsWith('http')) {
          imageUrl = getFullImageUrl(imageUrl);
        }
        setAboutUsImage(imageUrl);
        setTempText(data.aboutUs || '');
      } else {
        setHasData(false);
        setAboutUsId(null);
        setAboutUsText('');
        setAboutUsImage('');
        setTempText('');
      }
    } catch (error) {
      console.error("Error fetching about us:", error);
      if (error.message?.includes("404") || error.message?.toLowerCase().includes("not found")) {
        setHasData(false);
        setAboutUsId(null);
        setAboutUsText('');
        setAboutUsImage('');
        setTempText('');
      }
    } finally {
      hideLoader();
    }
  };

  // Handle Edit Click - Open Modal
  const handleEditClick = () => {
    setTempText(aboutUsText);
    setTempImage(aboutUsImage);
    setTempImageFile(null);
    setShowModal(true);
  };

  // Handle Modal Close
  const handleCloseModal = () => {
    if (!loading) {
      setShowModal(false);
      setTempText(aboutUsText);
      setTempImage(null);
      setTempImageFile(null);
    }
  };

  // Handle Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showNotification("Please select a valid image file", "warning");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification("Image size should be less than 5MB", "warning");
        return;
      }

      setTempImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => setTempImage(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Handle text change with character limit
  const handleTextChange = (e) => {
    const text = e.target.value;
    if (text.length <= charLimit) {
      setTempText(text);
    }
  };

  // Update character limit
  const handleCharLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setCharLimit(newLimit);
    // Optional: Save to localStorage or backend
    localStorage.setItem('aboutUsCharLimit', newLimit);
  };

  // Load saved character limit on mount
  useEffect(() => {
    const savedLimit = localStorage.getItem('aboutUsCharLimit');
    if (savedLimit) {
      setCharLimit(parseInt(savedLimit));
    }
    fetchAboutUs();
  }, []);

  const remainingChars = charLimit - tempText.length;
  const isNearLimit = remainingChars <= 50;
  const isOverLimit = tempText.length > charLimit;

  return (
    <div className="about-us-container">
      {snackbar.show && (
        <div className={`about-us-snackbar about-us-snackbar-${snackbar.type}`}>
          {snackbar.message}
        </div>
      )}

      <div className="about-us-card">
        {/* Top Right Edit Button */}
        <div className="about-us-actions">
          <button
            className="about-us-edit-btn"
            onClick={handleEditClick}
            disabled={loading}
          >
            <svg className="about-us-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
            </svg>
            EDIT
          </button>
        </div>

        <div className="about-us-grid">
          {/* Image Section */}
          <div className="about-us-image-section">
            <div className="about-us-image-wrapper">
              <div className="about-us-image-container">
                {aboutUsImage ? (
                  <img
                    src={aboutUsImage}
                    alt="About Us"
                    className="about-us-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '';
                    }}
                  />
                ) : (
                  <div className="about-us-image-placeholder">
                    <svg className="about-us-placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Text Section */}
          <div className="about-us-text-section">
            <div className="about-us-text-wrapper">
              <h2 className="about-us-heading">About Our Company</h2>

              <div className="about-us-text-display">
                <p>{aboutUsText || " "}</p>
                {/* <p>{aboutUsText || "Welcome to our company. We are committed to delivering high-quality services and innovative solutions that help businesses and individuals grow and succeed. Our mission is to provide excellence in every service we offer while building long-term, trustworthy relationships with our clients."}</p> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Editing */}
      {showModal && (
        <div className="about-us-modal-overlay" onClick={handleCloseModal}>
          <div className="about-us-modal" onClick={(e) => e.stopPropagation()}>
            {/* <div className="about-us-modal-header">
              <h3>Edit About Us</h3>
              <button className="about-us-modal-close" onClick={handleCloseModal} disabled={loading}>
                ×
              </button>
            </div> */}

            <div className="about-us-modal-body">
              {/* Image Upload Section */}
              <div className="about-us-modal-image-section">
                {/* <label className="about-us-modal-label">
                  Company Image
                </label> */}
                <div className="about-us-modal-image-container">
                  {tempImage ? (
                    <div className="about-us-modal-image-preview-wrapper">
                      <img src={tempImage} alt="Preview" className="about-us-modal-image-preview" />
                      <button
                        className="about-us-modal-image-remove"
                        onClick={() => {
                          setTempImage(null);
                          setTempImageFile(null);
                        }}
                        type="button"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="about-us-modal-image-placeholder">
                      <svg className="about-us-placeholder-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>No image selected</span>
                    </div>
                  )}
                  <button className="about-us-modal-upload-btn">
                    <svg className="about-us-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {tempImage ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="about-us-file-input"
                      disabled={loading}
                    />
                  </button>
                </div>
              </div>

              {/* Character Limit Settings */}
              {/* <div className="about-us-modal-limit-section">
                <label className="about-us-modal-label">
                  Character Limit
                </label>
                <select 
                  className="about-us-modal-limit-select"
                  value={charLimit}
                  onChange={handleCharLimitChange}
                  disabled={loading}
                >
                  <option value="300">300 characters</option>
                  <option value="500">500 characters</option>
                  <option value="1000">1000 characters</option>
                  <option value="1500">1500 characters</option>
                  <option value="2000">2000 characters</option>
                </select>
              </div> */}

              {/* Text Edit Section */}
              <div className="about-us-modal-text-section">
                <label className="about-us-modal-label">
                  Tell your story
                </label>
                <textarea
                  className={`about-us-modal-textarea ${isOverLimit ? 'about-us-modal-textarea-error' : ''}`}
                  value={tempText}
                  onChange={handleTextChange}
                  placeholder="Tell your customers about your restaurant, your story, your values, and what makes you special..."
                  rows="6"
                  disabled={loading}
                />

                {/* Character Counter */}
                <div className={`about-us-modal-char-counter ${isNearLimit ? 'near-limit' : ''} ${isOverLimit ? 'over-limit' : ''}`}>
                  <span className="about-us-modal-char-count">
                    {tempText.length} / {charLimit} characters
                  </span>
                  {isNearLimit && !isOverLimit && (
                    <span className="about-us-modal-char-warning">⚠️ Near limit</span>
                  )}
                  {isOverLimit && (
                    <span className="about-us-modal-char-error">❌ Exceeds limit by {tempText.length - charLimit} characters</span>
                  )}
                </div>
              </div>
            </div>

            <div className="about-us-modal-footer">
              <button
                className="about-us-modal-cancel-btn"
                onClick={handleCloseModal}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="about-us-modal-save-btn"
                onClick={handleSave}
                disabled={loading || isOverLimit || !tempText.trim()}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;